<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { VisuallyHidden } from 'radix-vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomButton from '@/components/ui/CustomButton.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { i18n } from '@/plugins/internationalization/i18n';
import ApiClient from '@/plugins/store/actions/api/ApiClient';
import { queryKeys } from '@/plugins/store/actions/queries/study-periods/index';

interface CreateState {
    comparisonType: 'within' | 'between';
    categoryIds: number[];
    animalGroupId?: number;
    studyPeriodIds: number[];
    favoriteTeamIds: number[];
    growthReferenceId?: number;
}

interface Props {
    comparisonType: 'within' | 'between';
    categoryNames: string[];
    animalGroupName: string;
    studyPeriodNames: string[];
    favoriteTeamNames: string[];
    growthReferenceName: string;
    state: CreateState;
}

const props = defineProps<Props>();

const toast = useToast();

const displayComparisonType = computed(() => {
    switch (props.comparisonType) {
        case 'within':
            return i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.singleTeam.label');
        case 'between':
            return i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.multipleTeams.label');
        default:
            return '';
    }
});

const isOpen = defineModel<boolean>('open', { required: true });

const name = ref('');

watch(isOpen, (newVal) => {
    if (!newVal) {
        name.value = '';
    }
});

const isFormValid = computed(() => name.value.length > 0);

const isLoading = ref(false);

const queryClient = useQueryClient();

async function createStudyPeriodSetup() {
    try {
        if (isLoading.value) {
            return;
        }

        isLoading.value = true;

        await ApiClient.send(
            'post',
            'monitor/study-periods/setup',
            {
                skipHandleError: true,
            },
            {
                name: name.value,
                type: props.state.comparisonType,
                animal_group_id: props.state.animalGroupId,
                categories: props.state.categoryIds,
                study_periods: props.state.studyPeriodIds,
                favorites: props.state.favoriteTeamIds,
                growth_reference: props.state.growthReferenceId,
            }
        );

        await queryClient.invalidateQueries({ queryKey: queryKeys.getStudyPeriodSetups() });

        toast.success(i18n.global.t('views.monitoring.teamComparison.dialogs.saveAnalysis.successMessage'));

        isOpen.value = false;
    } catch (err) {
        toast.error(i18n.global.t('views.monitoring.teamComparison.dialogs.saveAnalysis.errorMessage'));
    } finally {
        isLoading.value = false;
    }
}
</script>

<template>
    <Dialog v-model="isOpen" :disabled="isLoading">
        <DialogHeader>
            <DialogTitle>
                {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.title') }}
            </DialogTitle>
            <VisuallyHidden>
                <DialogDescription>
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.description') }}
                </DialogDescription>
            </VisuallyHidden>
        </DialogHeader>
        <div class="pt-2 space-y-4">
            <CustomInput
                v-model:value="name as any"
                :rules="[{ validate: (value: string) => value.length > 0, message: '' }]"
                required
                :label="$t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.name.label')"
            />
            <div class="rounded-md border border-gray-200 px-5 py-3 opacity-60">
                <div class="text-gray-500 text-sm font-medium">
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.comparisonType.label') }}
                </div>
                <span>
                    {{ displayComparisonType }}
                </span>
            </div>
            <div v-if="categoryNames.length" class="rounded-md border border-gray-200 px-4 py-2 opacity-60">
                <div class="text-gray-500 text-sm font-medium">
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.categories.label') }}
                </div>
                <span>
                    {{ categoryNames.join(', ') }}
                </span>
            </div>
            <div class="rounded-md border border-gray-200 px-4 py-2 opacity-60">
                <div class="text-gray-500 text-sm font-medium">
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.animalGroup.label') }}
                </div>
                <span>
                    {{ animalGroupName }}
                </span>
            </div>
            <div class="rounded-md border border-gray-200 px-4 py-2 opacity-60">
                <div class="text-gray-500 text-sm font-medium">
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.teams.label') }}
                </div>
                <span>
                    {{ studyPeriodNames.join(', ') }}
                </span>
            </div>
            <div v-if="favoriteTeamNames.length" class="rounded-md border border-gray-200 px-4 py-2 opacity-60">
                <div class="text-gray-500 text-sm font-medium">
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.favoriteTeams.label') }}
                </div>
                <span>
                    {{ favoriteTeamNames.join(', ') }}
                </span>
            </div>
            <div class="rounded-md border border-gray-200 px-4 py-2 opacity-60">
                <div class="text-gray-500 text-sm font-medium">
                    {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.fields.growthReference.label') }}
                </div>
                <span>
                    {{ growthReferenceName }}
                </span>
            </div>
        </div>
        <DialogFooter class="flex sm:justify-center pt-2">
            <DialogClose as-child>
                <CustomButton color-preset="white" class="min-w-44" :disabled="isLoading">{{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.buttons.cancel') }}</CustomButton>
            </DialogClose>
            <CustomButton class="min-w-44" :loading="isLoading" :disabled="!isFormValid" @click="createStudyPeriodSetup">
                {{ $t('views.monitoring.teamComparison.dialogs.saveAnalysis.buttons.save') }}
            </CustomButton>
        </DialogFooter>
    </Dialog>
</template>
